import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router';

/**
 * The design system's components, over the classes in `ui/system.css`.
 *
 * Pages build from these rather than from class names, so that a button is a
 * button everywhere: the old panel drew one as a link here, an outlined pill
 * there and an ink block somewhere else, and nothing said which was right. The
 * illustrated reference is `.docs/design/parent-v2/a-kece-kagit.html#kurallar`.
 */

// ---------------------------------------------------------------------------
// Icons

export type IconName =
  | 'panel'
  | 'lib'
  | 'plus'
  | 'child'
  | 'tablet'
  | 'ruler'
  | 'pause'
  | 'play'
  | 'stop'
  | 'check'
  | 'search'
  | 'camera'
  | 'lock'
  | 'info'
  | 'alert'
  | 'down'
  | 'chev'
  | 'sun'
  | 'hourglass'
  | 'wait'
  | 'x'
  | 'yarn'
  | 'spark'
  | 'grip'
  | 'pen'
  | 'up'
  | 'dn'
  | 'left'
  | 'right'
  | 'back'
  | 'chart';

/** One icon from `public/icons.svg`. Decorative: the text beside it carries the meaning. */
export const Icon = ({ name, small = false }: { name: IconName; small?: boolean }) => (
  <svg className={small ? 'ic sm' : 'ic'} aria-hidden="true" focusable="false">
    <use href={`/icons.svg#i-${name}`} />
  </svg>
);

// ---------------------------------------------------------------------------
// Buttons

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'amber';

interface ButtonLook {
  variant?: ButtonVariant;
  small?: boolean;
  block?: boolean;
  icon?: IconName;
}

const buttonClass = ({ variant = 'secondary', small, block }: ButtonLook, extra?: string) =>
  ['btn', `btn-${variant}`, small ? 'btn-sm' : '', block ? 'btn-block' : '', extra ?? '']
    .filter(Boolean)
    .join(' ');

/**
 * Primary is the one thing a screen is for and appears once per view. Secondary
 * is every other action. Ghost is for "cancel" and "show more". Danger asks
 * before it does anything that cannot be undone.
 */
export const Button = ({
  variant,
  small,
  block,
  icon,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonLook & ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button type={type} className={buttonClass({ variant, small, block }, className)} {...rest}>
    {icon === undefined ? null : <Icon name={icon} small={small} />}
    {children}
  </button>
);

/** A navigation that looks like a button. Same rules as `Button`. */
export const ButtonLink = ({
  variant,
  small,
  block,
  icon,
  className,
  children,
  ...rest
}: ButtonLook & LinkProps) => (
  <Link className={buttonClass({ variant, small, block }, className)} {...rest}>
    {icon === undefined ? null : <Icon name={icon} small={small} />}
    {children}
  </Link>
);

// ---------------------------------------------------------------------------
// Surfaces

/** A grouped piece of a page. A heading, if any, goes in `title`. */
export const Card = ({
  title,
  meta,
  aside,
  children,
  className,
}: {
  title?: ReactNode;
  meta?: ReactNode;
  aside?: ReactNode;
  children?: ReactNode;
  className?: string;
}) => (
  <article className={['card', className ?? ''].filter(Boolean).join(' ')}>
    {title === undefined && aside === undefined ? null : (
      <div className="card-head">
        <div>
          {title === undefined ? null : <h2 className="t-h2">{title}</h2>}
          {meta === undefined ? null : <p className="t-meta">{meta}</p>}
        </div>
        {aside}
      </div>
    )}
    {children}
  </article>
);

/** A label and its control, on one line. The workhorse of every settings card. */
export const Row = ({
  label,
  detail,
  children,
}: {
  label: ReactNode;
  detail?: ReactNode;
  children?: ReactNode;
}) => (
  <div className="row">
    <div className="row-text">
      <b>{label}</b>
      {detail === undefined ? null : <span>{detail}</span>}
    </div>
    {children}
  </div>
);

export type Tone = 'sage' | 'amber' | 'clay' | 'line';

/** Status, never decoration: sage is visible or done, amber in progress, clay a problem. */
export const Badge = ({ tone = 'line', children }: { tone?: Tone; children: ReactNode }) => (
  <span className={`badge badge-${tone}`}>{children}</span>
);

/** An empty list says what would be here and how to get it there, in one line. */
export const EmptyState = ({
  title,
  detail,
  actions,
}: {
  title: ReactNode;
  detail?: ReactNode;
  actions?: ReactNode;
}) => (
  <div className="empty">
    <b className="t-title">{title}</b>
    {detail === undefined ? null : <p className="t-meta">{detail}</p>}
    {actions === undefined ? null : <div className="btns">{actions}</div>}
  </div>
);

/** Progress through something with a known end: a download, a day's budget. */
export const ProgressBar = ({ value, label }: { value: number; label: string }) => (
  <div
    className="bar"
    role="progressbar"
    aria-label={label}
    aria-valuemin={0}
    aria-valuemax={100}
    aria-valuenow={Math.round(value * 100)}
  >
    <i style={{ width: `${Math.max(0, Math.min(1, value)) * 100}%` }} />
  </div>
);

// ---------------------------------------------------------------------------
// Choice

/** A selectable pill. Several together are a multi-select; see `Segmented` for one-of. */
export const Chip = ({
  selected,
  onToggle,
  disabled,
  children,
}: {
  selected: boolean;
  onToggle: () => void;
  disabled?: boolean;
  children: ReactNode;
}) => (
  <button
    type="button"
    className="chip"
    aria-pressed={selected}
    disabled={disabled}
    onClick={onToggle}
  >
    <svg className="ic sm chip-check" aria-hidden="true" focusable="false">
      <use href="/icons.svg#i-check" />
    </svg>
    {children}
  </button>
);

/** One of a few, side by side. More than four options belong in a list. */
export const Segmented = <Value extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: Value;
  options: { value: Value; label: ReactNode }[];
  onChange: (value: Value) => void;
}) => (
  <fieldset className="seg" aria-label={label}>
    {options.map((option) => (
      <button
        key={option.value}
        type="button"
        aria-pressed={option.value === value}
        onClick={() => onChange(option.value)}
      >
        {option.label}
      </button>
    ))}
  </fieldset>
);

/** On or off, saved the moment it moves. Sage when on. */
export const Switch = ({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
}) => (
  <button
    type="button"
    className="switch"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!checked)}
  />
);

/** A number in fixed steps between bounds. The value is shown, never typed. */
export const Stepper = ({
  value,
  display,
  min,
  max,
  step = 1,
  onChange,
  decreaseLabel,
  increaseLabel,
}: {
  value: number;
  display: ReactNode;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
  decreaseLabel: string;
  increaseLabel: string;
}) => (
  <div className="stepper">
    <button
      type="button"
      aria-label={decreaseLabel}
      disabled={value <= min}
      onClick={() => onChange(Math.max(min, value - step))}
    >
      −
    </button>
    <output className="num">{display}</output>
    <button
      type="button"
      aria-label={increaseLabel}
      disabled={value >= max}
      onClick={() => onChange(Math.min(max, value + step))}
    >
      +
    </button>
  </div>
);
